import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import * as yup from 'yup';
import { formatEditorHtmlToMessageText } from 'src/components/TemplateText/helpers';
import { convertHtmlToText } from 'src/helpers/helpers';
import {
  normalizeRecurringSettings,
  normalizeTextToWinSettings,
} from 'src/modules/campaigns/helpers';
import { CampaignFormValues, FormMessage } from '../types';
import {
  CampaignIssue,
  CampaignValidator,
  collectSchemaIssues,
  validateSchema,
} from '../policies/issues';

/** Field path -> message, e.g. `{ name: 'Name is required' }`. */
export type FieldErrors = Record<string, string>;

/**
 * One rule the schema reported. `type` is yup's rule name — the draft gate needs
 * it to tell "this field is empty" (which a draft may carry) from "this value is
 * wrong" (which it may not).
 */
export type FieldIssue = CampaignIssue;

type Action =
  | { type: 'setField'; name: keyof CampaignFormValues; value: unknown }
  | { type: 'updateMessage'; index: number; patch: Partial<FormMessage> }
  | { type: 'setMessages'; messages: FormMessage[] }
  | { type: 'reset'; values: CampaignFormValues };

function reducer(state: CampaignFormValues, action: Action): CampaignFormValues {
  switch (action.type) {
    case 'setField':
      return { ...state, [action.name]: action.value };
    case 'updateMessage':
      return {
        ...state,
        scheduledMessages: state.scheduledMessages.map((m, i) =>
          (i === action.index ? { ...m, ...action.patch } : m)),
      };
    case 'setMessages':
      return { ...state, scheduledMessages: action.messages };
    case 'reset':
      return action.values;
    default:
      return state;
  }
}

/** Flatten a yup error tree into a flat list of issues. */
export function collectIssues(error: unknown): FieldIssue[] {
  return collectSchemaIssues(error);
}

type ValidationSource = yup.AnyObjectSchema | CampaignValidator;

/** Validate now. Every rule in this module is synchronous, so nothing waits. */
function validateNow(source: ValidationSource, values: CampaignFormValues): FieldIssue[] {
  return typeof source === 'function' ? source(values) : validateSchema(source, values);
}

/** Compare semantic values, not editor HTML. */
export function serializeComparableValues(values: CampaignFormValues): string {
  const textToWinSettings = values.textToWinSettings
    ? normalizeTextToWinSettings(values.textToWinSettings)
    : undefined;
  const comparable = {
    ...values,
    recurringSettings: values.recurringSettings
      ? normalizeRecurringSettings(values.recurringSettings)
      : undefined,
    textToWinSettings: textToWinSettings
      ? {
          ...textToWinSettings,
          entrySuccessMessageText: convertHtmlToText(textToWinSettings.entrySuccessMessageText),
          entryIntervalLimitMessageText: convertHtmlToText(textToWinSettings.entryIntervalLimitMessageText ?? ''),
          entryTotalLimitMessageText: convertHtmlToText(textToWinSettings.entryTotalLimitMessageText ?? ''),
          entryNotStartedMessageText: convertHtmlToText(textToWinSettings.entryNotStartedMessageText ?? ''),
          entryEndedMessageText: convertHtmlToText(textToWinSettings.entryEndedMessageText ?? ''),
          winnerMessageText: convertHtmlToText(textToWinSettings.winnerMessageText),
          nonWinnerMessageText: convertHtmlToText(textToWinSettings.nonWinnerMessageText ?? ''),
        }
      : undefined,
    scheduledMessages: values.scheduledMessages.map(({ clientKey, ...message }) => {
      void clientKey;
      return {
        ...message,
        text: formatEditorHtmlToMessageText(message.text),
        isCaptureContact: message.isCaptureContact || undefined,
      };
    }),
  };
  return JSON.stringify(comparable);
}

export interface CampaignForm {
  values: CampaignFormValues;
  /**
   * The current values, including changes dispatched during this same handler.
   * `values` is the rendered snapshot and lags by one render; anything that
   * changes a field and then saves must read this instead.
   */
  getValues: () => CampaignFormValues;
  /** Only the fields the user has already left, or everything after a submit. */
  errors: FieldErrors;
  /**
   * Every rule the schema currently reports, whether shown or not. The draft
   * gate reads these rather than validating a second time.
   */
  issues: FieldIssue[];
  setField: <K extends keyof CampaignFormValues>(name: K, value: CampaignFormValues[K]) => void;
  /**
   * Apply an asynchronously-resolved default without making a fresh form dirty.
   * Only this field joins the baseline; unrelated user edits remain unsaved.
   */
  setInitialField: <K extends keyof CampaignFormValues>(
    name: K,
    value: CampaignFormValues[K],
  ) => void;
  updateMessage: (index: number, patch: Partial<FormMessage>) => void;
  setMessages: (messages: FormMessage[]) => void;
  /** Show this field's error now that the user has left it. */
  blur: (name: string) => void;
  /** Server-side errors, e.g. a name the API says is taken. */
  setFieldErrors: (errors: FieldErrors) => void;
  /** What the schema says right now. Changes nothing on screen. */
  validate: () => FieldIssue[];
  /**
   * Stop hiding errors for fields the user has not visited. Both old-builder
   * save paths marked the form attempted; draft validation still decides which
   * issues are allowed to pass.
   */
  revealAll: () => void;
  /** Accept the given values as the new saved state. */
  reset: (values: CampaignFormValues) => void;
  isDirty: boolean;
  /** True once a save has revealed every error. */
  isSubmitted: boolean;
}

/**
 * Form state for the builder: a reducer for the values, yup for the rules.
 *
 * Errors are computed from the whole schema whenever the values change, but a
 * field only shows its error once the user has left it — a fresh campaign should
 * not open covered in red. A save attempt reveals the rest.
 */
export function useCampaignFormState(
  initialValues: CampaignFormValues,
  validation: ValidationSource,
): CampaignForm {
  // The reducer runs once, in `apply`; React only takes the result. That keeps
  // `values` and `latest.current` the same object, and lets a handler read its
  // own change back before the re-render.
  const [values, replace] = useReducer(
    (_: CampaignFormValues, next: CampaignFormValues) => next,
    initialValues,
  );
  const latest = useRef(values);
  const apply = useCallback((action: Action) => {
    latest.current = reducer(latest.current, action);
    replace(latest.current);
  }, []);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  // State, not a ref: `reset` is handed the very object the form already holds,
  // so React skips the value update and only a real dependency makes isDirty
  // recompute.
  const [baseline, setBaseline] = useState(() => serializeComparableValues(initialValues));
  const baselineValues = useRef(initialValues);

  // One validation pass per change, shared by the messages shown under the
  // fields and by the Save as Draft gate. Synchronous, so nothing lags behind
  // the keystroke, and a memo cannot loop the way a validating effect could.
  const issues = useMemo(() => validateNow(validation, values), [validation, values]);
  const isDirty = useMemo(() => serializeComparableValues(values) !== baseline, [values, baseline]);

  const errors = useMemo(() => {
    const visible: FieldErrors = {};
    for (const issue of issues) {
      if (issue.visible && !visible[issue.path] && (showAll || touched.has(issue.path))) {
        visible[issue.path] = issue.message;
      }
    }
    // A server error outlives the next keystroke; the user has to change the field.
    return { ...visible, ...serverErrors };
  }, [issues, touched, showAll, serverErrors]);

  const setField = useCallback<CampaignForm['setField']>((name, value) => {
    setServerErrors(prev => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name as string];
      return next;
    });
    apply({ type: 'setField', name, value });
  }, [apply]);

  const setInitialField = useCallback<CampaignForm['setInitialField']>((name, value) => {
    baselineValues.current = reducer(baselineValues.current, { type: 'setField', name, value });
    setBaseline(serializeComparableValues(baselineValues.current));
    apply({ type: 'setField', name, value });
  }, [apply]);

  const updateMessage = useCallback(
    (index: number, patch: Partial<FormMessage>) => apply({ type: 'updateMessage', index, patch }),
    [apply],
  );

  const setMessages = useCallback(
    (messages: FormMessage[]) => apply({ type: 'setMessages', messages }),
    [apply],
  );

  const blur = useCallback(
    (name: string) => setTouched(prev => (prev.has(name) ? prev : new Set(prev).add(name))),
    [],
  );

  const validate = useCallback(() => validateNow(validation, latest.current), [validation]);
  const revealAll = useCallback(() => setShowAll(true), []);

  const reset = useCallback((next: CampaignFormValues) => {
    baselineValues.current = next;
    setBaseline(serializeComparableValues(next));
    apply({ type: 'reset', values: next });
    setTouched(new Set());
    setServerErrors({});
    setShowAll(false);
  }, [apply]);

  return {
    values,
    getValues: useCallback(() => latest.current, []),
    errors,
    issues,
    setField,
    setInitialField,
    updateMessage,
    setMessages,
    blur,
    setFieldErrors: setServerErrors,
    validate,
    revealAll,
    reset,
    isDirty,
    isSubmitted: showAll,
  };
}
